import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Paper, Chip, Button, List, ListItem,
  ListItemButton, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Divider
} from "@mui/material";
import {
  Save as SaveIcon, Refresh as ResetIcon, Add as AddIcon,
  Close as CloseIcon, Block as BlockIcon, CheckCircle as UnblockIcon,
} from "@mui/icons-material";
import { fetchData, putData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

// Role status toggle — §27
const toggleRoleStatus = async (role, localRoles, setLocalRoles, setToast) => {
  const newStatus = role.status === "Inactive" ? "Active" : "Inactive";
  const roleKey   = role.id || encodeURIComponent(role.name || "");
  try {
    await putData(`admin/role-config/roles/${roleKey}/status`, { status: newStatus });
  } catch (_) { /* Optimistic update — API may not exist yet */ }
  setLocalRoles(prev =>
    prev.map(r => r.id === role.id && r.name === role.name ? { ...r, status: newStatus } : r)
  );
  setToast({ open: true, severity: "success", message: `Role ${newStatus === "Inactive" ? "deactivated" : "reactivated"} successfully` });
};

const FALLBACK_ROLES = [
  { id: 1, name: "Admin", badge: "Super", badgeColor: "#9e9e9e" },
  { id: 2, name: "Academic Coordinator", badge: "Staff", badgeColor: "#2196f3" },
  { id: 3, name: "Finance Admin", badge: "Staff", badgeColor: "#2196f3" },
  { id: 4, name: "Secretary", badge: "Staff", badgeColor: "#2196f3" },
  { id: 5, name: "Teacher", badge: "Staff", badgeColor: "#2196f3" },
  { id: 6, name: "Para Professional", badge: "Staff", badgeColor: "#2196f3" },
  { id: 7, name: "Student", badge: "Limited", badgeColor: "#9e9e9e" },
];

const MODULES = [
  { name: "Admission Module", permissions: ["View", "Create", "Edit", "Approve"] },
  { name: "Progress Reports", permissions: ["View", "Create", "Edit", "Upload"] },
  { name: "Attendance", permissions: ["View", "Create", "Edit", "Approve"] },
  { name: "Student List", permissions: ["View", "Create", "Edit"] },
  { name: "Biliteracy Evaluation", permissions: ["View", "Create", "Edit", "Approve"] },
  { name: "Question Paper", permissions: ["View", "Create", "Edit", "Upload"] },
  { name: "Google Classroom", permissions: ["View", "Create"] },
  { name: "Document Storage", permissions: ["View", "Upload", "Edit"] },
  { name: "Leave Management", permissions: ["View", "Create", "Approve"] },
  { name: "Pickup/Drop", permissions: ["View", "Create", "Edit"] },
  { name: "Complaint Management", permissions: ["View", "Create", "Edit", "Approve"] }
];

const EMPTY_ADD_ROLE_FORM = {
  roleName: "", status: "Active", module: "", subModule: "", roleDescription: ""
};

const AdminRoleConfig = () => {
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(0);
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  // Access Duration state
  const [accessDuration, setAccessDuration] = useState({
    registrationFrom: "", registrationTo: "",
    volunteerFrom: "", volunteerTo: ""
  });

  // Add Role dialog state
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [addRoleForm, setAddRoleForm] = useState(EMPTY_ADD_ROLE_FORM);
  const [addRoleLoading, setAddRoleLoading] = useState(false);
  const [, setRoles] = useState([]);
  const [localRoles, setLocalRoles] = useState([]);

  const selectedRole = localRoles[selectedRoleIndex] || {};

  const loadRoles = useCallback(async () => {
    try {
      const res = await fetchData("admin/role-config/roles");
      const rawRoles = res?.data?.roles || res?.data || res || [];
      const rolesArray = Array.isArray(rawRoles) ? rawRoles : [];
      const superAdminNames = ["super-admin", "admin", "super admin"];
      const mapped = rolesArray.map(r => ({
        ...r,
        badge: superAdminNames.includes((r.name || "").toLowerCase()) ? "Super" : "Staff",
        badgeColor: superAdminNames.includes((r.name || "").toLowerCase()) ? "#9e9e9e" : "#2196f3"
      }));
      setRoles(mapped.length ? mapped : FALLBACK_ROLES);
      setLocalRoles(mapped.length ? mapped : FALLBACK_ROLES);
    } catch {
      setRoles(FALLBACK_ROLES);
      setLocalRoles(FALLBACK_ROLES);
    }
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const loadPermissionsForRole = useCallback(async (roleId) => {
    const defaults = {};
    MODULES.forEach(m => {
      defaults[m.name] = {};
      m.permissions.forEach(p => { defaults[m.name][p] = false; });
    });
    try {
      const result = await fetchData(`admin/role-config/roles/${roleId}/permissions`);
      if (result?.data) {
        // DB stores permissions as array of {module, permissions:{}} records
        const rawPerms = result.data.permissions;
        let perms = defaults;
        if (Array.isArray(rawPerms) && rawPerms.length > 0) {
          // Convert array [{module, permissions:{view:true,...}}] → nested object
          perms = { ...defaults };
          rawPerms.forEach(p => {
            if (p.module) perms[p.module] = p.permissions || {};
          });
        } else if (rawPerms && !Array.isArray(rawPerms)) {
          // Already a nested object
          perms = rawPerms;
        }
        setPermissions(perms);
        setOriginalPermissions(perms);
        if (result.data.accessDuration) {
          setAccessDuration(result.data.accessDuration);
        }
      } else {
        setPermissions(defaults);
        setOriginalPermissions(defaults);
      }
    } catch {
      setPermissions(defaults);
      setOriginalPermissions(defaults);
    }
  }, []);

  useEffect(() => {
    const role = localRoles[selectedRoleIndex];
    if (!role?.id) return;
    loadPermissionsForRole(role.id);
  }, [selectedRoleIndex, localRoles, loadPermissionsForRole]);

  const togglePermission = (moduleName, permission) => {
    setPermissions(prev => ({
      ...prev,
      [moduleName]: {
        ...prev[moduleName],
        [permission]: !(prev[moduleName]?.[permission])
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const role = localRoles[selectedRoleIndex];
      const roleKey = role?.id || encodeURIComponent(role?.name || "");
      // Convert nested object { moduleName: { perm: bool } } → array expected by backend
      const permissionsArray = Object.entries(permissions).map(([module, perms]) => ({
        module,
        permissions: perms
      }));
      await putData(`admin/role-config/roles/${roleKey}/permissions`, {
        permissions: permissionsArray,
        accessDuration
      });
      setToast({ open: true, severity: "success", message: "Permissions saved successfully" });
      setOriginalPermissions(permissions);
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to save configuration" });
    }
    setLoading(false);
  };

  const handleReset = () => {
    setPermissions(originalPermissions);
    setToast({ open: true, severity: "info", message: "Configuration reset" });
  };

  const handleAccessDurationChange = (field) => (e) => {
    setAccessDuration(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddRoleChange = (field) => (e) => {
    setAddRoleForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddRoleSubmit = async (submitStatus) => {
    if (!addRoleForm.roleName) {
      setToast({ open: true, severity: "error", message: "Role Name is required" });
      return;
    }
    setAddRoleLoading(true);
    try {
      const payload = { ...addRoleForm, approvalStatus: submitStatus };
      await postData("admin/role-config/create", payload);
      setToast({ open: true, severity: "success", message: `Role ${submitStatus.toLowerCase()} successfully` });
      const newRole = { name: addRoleForm.roleName, badge: "Staff", badgeColor: "#2196f3" };
      setRoles(prev => [...prev, newRole]);
      setLocalRoles(prev => [...prev, newRole]);
      setAddRoleOpen(false);
      setAddRoleForm(EMPTY_ADD_ROLE_FORM);
    } catch (err) {
      // Fallback: add to local state only
      const newRole = { name: addRoleForm.roleName, badge: "Staff", badgeColor: "#2196f3" };
      setRoles(prev => [...prev, newRole]);
      setLocalRoles(prev => [...prev, newRole]);
      setToast({ open: true, severity: "success", message: "Role added locally" });
      setAddRoleOpen(false);
      setAddRoleForm(EMPTY_ADD_ROLE_FORM);
    }
    setAddRoleLoading(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Role Configuration</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Role Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddRoleOpen(true)}
          sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}
        >
          + Add
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Panel - Role List */}
        <Grid item xs={12} md={3.5}>
          <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ p: 2, backgroundColor: "#f5f3ff" }}>
              <Typography variant="subtitle1" fontWeight="bold">Roles</Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {localRoles.map((role, idx) => {
                const isInactive = role.status === "Inactive";
                return (
                  <ListItem key={role.name + idx} disablePadding>
                    <ListItemButton
                      selected={selectedRoleIndex === idx}
                      onClick={() => setSelectedRoleIndex(idx)}
                      sx={{
                        opacity: isInactive ? 0.55 : 1,
                        "&.Mui-selected": { backgroundColor: "#EAF1F8", borderLeft: "3px solid #244C77" },
                        "&.Mui-selected:hover": { backgroundColor: "#EAF1F8" }
                      }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={selectedRoleIndex === idx ? 600 : 400} sx={{ color: isInactive ? "#9e9e9e" : "inherit" }}>
                              {role.name}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                              {isInactive && (
                                <Chip label="Inactive" size="small" sx={{ backgroundColor: "#f5f5f5", color: "#9e9e9e", fontWeight: 600, fontSize: 9, height: 20 }} />
                              )}
                              <Chip label={role.badge} size="small" sx={{
                                backgroundColor: role.badgeColor + "20",
                                color: role.badgeColor,
                                fontWeight: 600, fontSize: 10, height: 22
                              }} />
                              <IconButton
                                size="small"
                                title={isInactive ? "Reactivate" : "Deactivate"}
                                onClick={e => { e.stopPropagation(); toggleRoleStatus(role, localRoles, setLocalRoles, setToast); }}
                                sx={{ p: 0.3, color: isInactive ? "#4caf50" : "#f44336" }}
                              >
                                {isInactive
                                  ? <UnblockIcon sx={{ fontSize: "16px" }} />
                                  : <BlockIcon    sx={{ fontSize: "16px" }} />}
                              </IconButton>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Right Panel - Permissions */}
        <Grid item xs={12} md={8.5}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Configuring: {selectedRole.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Toggle permissions for each module
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" startIcon={<ResetIcon />} onClick={handleReset}
                  sx={{ textTransform: "none", color: "#9e9e9e", borderColor: "#e0e0e0" }}>
                  Reset
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}
                  disabled={loading}
                  sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
                  {loading ? "Saving..." : "Save Configuration"}
                </Button>
              </Box>
            </Box>

            {MODULES.map((module) => (
              <Box key={module.name} sx={{
                p: 2, mb: 2, borderRadius: 2, border: "1px solid #e0e0e0",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                "&:hover": { backgroundColor: "#fafafa" }
              }}>
                <Typography variant="body2" fontWeight={500} sx={{ minWidth: 200 }}>
                  {module.name}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {module.permissions.map((perm) => {
                    const isActive = permissions[module.name]?.[perm] || false;
                    return (
                      <Chip key={perm} label={perm} clickable size="small"
                        onClick={() => togglePermission(module.name, perm)}
                        sx={{
                          backgroundColor: isActive ? "#244C77" : "#f5f5f5",
                          color: isActive ? "#fff" : "#757575",
                          fontWeight: 600,
                          border: isActive ? "none" : "1px solid #e0e0e0",
                          "&:hover": {
                            backgroundColor: isActive ? "#4030c0" : "#e0e0e0"
                          }
                        }} />
                    );
                  })}
                </Box>
              </Box>
            ))}

            {/* Access Duration Section */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Access Duration
            </Typography>

            {/* Registration Access */}
            <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
              <Typography variant="body2" fontWeight={600} mb={2} color="#244C77">
                Registration Access
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={accessDuration.registrationFrom}
                    onChange={handleAccessDurationChange("registrationFrom")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={accessDuration.registrationTo}
                    onChange={handleAccessDurationChange("registrationTo")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Volunteer Access */}
            <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
              <Typography variant="body2" fontWeight={600} mb={2} color="#244C77">
                Volunteer Access
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={accessDuration.volunteerFrom}
                    onChange={handleAccessDurationChange("volunteerFrom")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={accessDuration.volunteerTo}
                    onChange={handleAccessDurationChange("volunteerTo")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Role Dialog */}
      <Dialog open={addRoleOpen} onClose={() => setAddRoleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">Add New Role</Typography>
          <IconButton onClick={() => setAddRoleOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Role Name *"
                fullWidth size="small"
                value={addRoleForm.roleName}
                onChange={handleAddRoleChange("roleName")}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={addRoleForm.status} label="Status"
                  onChange={handleAddRoleChange("status")}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Module"
                fullWidth size="small"
                value={addRoleForm.module}
                onChange={handleAddRoleChange("module")}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub-Module</InputLabel>
                <Select value={addRoleForm.subModule} label="Sub-Module"
                  onChange={handleAddRoleChange("subModule")}>
                  {MODULES.map(m => (
                    <MenuItem key={m.name} value={m.name}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Role Description"
                fullWidth size="small"
                multiline rows={4}
                value={addRoleForm.roleDescription}
                onChange={handleAddRoleChange("roleDescription")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setAddRoleOpen(false); setAddRoleForm(EMPTY_ADD_ROLE_FORM); }}
            variant="outlined" color="inherit" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleAddRoleSubmit("Rejected")}
            variant="contained"
            disabled={addRoleLoading}
            sx={{
              backgroundColor: "#f44336",
              "&:hover": { backgroundColor: "#c62828" },
              textTransform: "none"
            }}
          >
            Rejected
          </Button>
          <Button
            onClick={() => handleAddRoleSubmit("Approved")}
            variant="contained"
            disabled={addRoleLoading}
            sx={{
              backgroundColor: "#4caf50",
              "&:hover": { backgroundColor: "#388e3c" },
              textTransform: "none"
            }}
          >
            Approved
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminRoleConfig;
